import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';

/**
 * Builds the `flowSync` / applySync payload body used by the slot list and other flow editors.
 * Must stay in sync with `List` `syncFlow` in list.tsx (single source of truth).
 */
export type BuildFlowSyncEmitPayloadOptions = {
  /** Slot reorder / content-only edits: keep eventSlotId for scope but omit schedule timing fields. */
  omitEventSlotTiming?: boolean;
};

export function buildFlowSyncEmitPayload(
  flow: FlowDto,
  eventSlotIdParam: string | null,
  options?: BuildFlowSyncEmitPayloadOptions,
) {
  let startTime: string | undefined;
  let endTime: string | null | undefined;
  let eventDayId: string | undefined;
  let eventSlotId: string | undefined;
  const omitEventSlotTiming = options?.omitEventSlotTiming === true;

  if (eventSlotIdParam) {
    eventSlotId = eventSlotIdParam ?? flow.slot?.id;
    if (!omitEventSlotTiming && flow.slot?.startOffsetMinutes !== undefined) {
      startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);

      const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
      if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
        endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
      } else {
        endTime = null;
      }
    }

    if (!omitEventSlotTiming && flow.slot?.day?.id) {
      eventDayId = flow.slot.day.id;
    }
  } else if (!omitEventSlotTiming) {
    startTime = minuteOffsetToTime(0);

    const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
    if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
      endTime = minuteOffsetToTime(flowDurationPlannedMinutes);
    } else {
      endTime = null;
    }
  }

  const normalizedSlots = flow.slots
    ?.filter((slot) => slot.contentType !== 'Gap')
    .map((slot) => {
      const fallbackPlannedDuration =
        slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0;
      const normalized = {
        ...slot,
        plannedDurationMinutes: fallbackPlannedDuration,
        content: typeof slot.content === 'object' ? slot.content?.id : slot.content,
      };
      if (slot.id?.startsWith('optimistic-')) {
        delete (normalized as { id?: string }).id;
      }
      return normalized;
    });

  // Strip schedule/timing keys from the spread — `...flow` would re-introduce them after omitEventSlotTiming.
  type FlowWithTimingFields = FlowDto & {
    startTime?: string;
    endTime?: string | null;
    eventDayId?: string;
    eventSlotId?: string;
  };
  const flowRest = { ...flow } as FlowWithTimingFields;
  delete flowRest.slot;
  delete flowRest.startDate;
  delete flowRest.startTime;
  delete flowRest.endTime;
  delete flowRest.eventDayId;
  delete flowRest.eventSlotId;

  return {
    flow: {
      ...flowRest,
      slots: normalizedSlots,
      ...(eventSlotId && { eventSlotId }),
      ...(!omitEventSlotTiming && startTime && { startTime }),
      ...(!omitEventSlotTiming && startTime !== undefined && { endTime: endTime ?? null }),
      ...(!omitEventSlotTiming && eventSlotId && eventDayId && { eventDayId }),
    },
  };
}
